import { Link, useLocalSearchParams, type Href } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, TextInput, View } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { Alert } from "@/src/lib/alert";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { useControlAccessContext } from "@/src/control/ControlAccessContext";
import { ControlDisclosure, ControlId, ControlKeyValue } from "@/src/control/ControlDetails";
import { MonetizationCampaignForm, campaignInputFromAdmin } from "@/src/control/MonetizationCampaignForm";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import {
  getSponsoredCampaign,
  reviewSponsoredCampaign,
  setSponsoredCampaignStatus,
  updateSponsoredCampaign,
  type SponsoredCampaignAdmin,
  type SponsoredCampaignInput,
  type SponsoredCampaignMetrics,
  type SponsoredCampaignStatus,
} from "@/src/control/api";
import { hasControlPermission } from "@/src/control/permissions";
import { action, border, radius, spacing, surface, text } from "@/src/theme/tokens";
import { campaignDeliveryStateLabel, campaignModerationStatusLabel, campaignStatusLabel, formatControlDate, formatSubjectList, formatTrackList } from "@/src/control/presentation";

export default function MonetizationCampaignScreen() {
  return <ControlAccessGate permission="monetization.read"><CampaignDetail /></ControlAccessGate>;
}

function CampaignDetail() {
  const params = useLocalSearchParams<{ campaignId?: string | string[] }>();
  const campaignId = useMemo(() => Array.isArray(params.campaignId) ? params.campaignId[0] : params.campaignId || "", [params.campaignId]);
  const access = useControlAccessContext();
  const canManage = access.status === "allowed" && hasControlPermission(access.member, "monetization.manage");
  const canModerate = access.status === "allowed" && hasControlPermission(access.member, "moderation.manage");
  const [campaign, setCampaign] = useState<SponsoredCampaignAdmin | null>(null);
  const [metrics, setMetrics] = useState<SponsoredCampaignMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reviewReason, setReviewReason] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    if (!campaignId) { setError(true); setLoading(false); return () => { active = false; }; }
    setLoading(true);
    setError(false);
    getSponsoredCampaign(campaignId)
      .then((value) => { if (active) { setCampaign(value.campaign); setMetrics(value.metrics); } })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt, campaignId]);

  async function setStatus(status: SponsoredCampaignStatus) {
    if (!campaign || busy) return;
    setBusy(true);
    try {
      await setSponsoredCampaignStatus(campaign.id, status);
      setAttempt((value) => value + 1);
    } catch (cause: unknown) {
      Alert.alert("Transition refusée", friendlyFirebaseError(cause, "La transition n’a pas pu être appliquée."));
    } finally { setBusy(false); }
  }

  async function save(input: SponsoredCampaignInput) {
    if (!campaign || busy) return;
    setBusy(true);
    try {
      await updateSponsoredCampaign(campaign.id, input);
      setEditing(false);
      setAttempt((value) => value + 1);
      Alert.alert("Campagne mise à jour", "La modification remet automatiquement le créatif en attente de modération.");
    } catch (cause: unknown) {
      Alert.alert("Modification refusée", friendlyFirebaseError(cause, "La campagne n’a pas pu être modifiée."));
    } finally { setBusy(false); }
  }

  async function review(decision: "approved" | "rejected") {
    if (!campaign || busy) return;
    const reason = reviewReason.trim();
    if (reason.length < 3) { Alert.alert("Motif requis", "Ajoute un motif de modération."); return; }
    setBusy(true);
    try {
      await reviewSponsoredCampaign(campaign.id, decision, reason);
      setReviewReason("");
      setAttempt((value) => value + 1);
    } catch (cause: unknown) {
      Alert.alert("Décision refusée", friendlyFirebaseError(cause, "La décision n’a pas pu être enregistrée."));
    } finally { setBusy(false); }
  }

  if (loading) return <State><ActivityIndicator color={text.brand} /></State>;
  if (error || !campaign || !metrics) return <State><Text style={styles.stateTitle}>Campagne indisponible</Text><Pressable accessibilityRole="button" onPress={() => setAttempt((value) => value + 1)}><Text style={styles.link}>Réessayer</Text></Pressable></State>;

  const editable = canManage && ["draft", "paused"].includes(campaign.status);
  const transitions = campaign.status === "draft"
    ? (["active", "ended"] as SponsoredCampaignStatus[])
    : campaign.status === "active"
      ? (["paused", "ended"] as SponsoredCampaignStatus[])
      : campaign.status === "paused"
        ? (["active", "ended"] as SponsoredCampaignStatus[])
        : [];

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <Link href={"/control/monetization" as Href} asChild><Pressable accessibilityRole="link"><Text style={styles.back}>‹ Retour aux campagnes</Text></Pressable></Link>
      <View style={styles.header}>
        <Text style={styles.kicker}>CAMPAGNE SPONSORISÉE</Text>
        <Text style={styles.title}>{campaign.headline || campaign.advertiserName || "Campagne sans titre"}</Text>
        <Text style={styles.subtitle}>{campaign.advertiserName}</Text>
        <View style={styles.badges}>
          <Badge label={`Campagne : ${campaignStatusLabel(campaign.status)}`} />
          <Badge label={`Validation : ${campaignModerationStatusLabel(campaign.moderationStatus)}`} />
          <Badge label={`Diffusion : ${campaignDeliveryStateLabel(campaign.deliveryState)}`} />
        </View>
      </View>

      <View style={styles.metrics}>
        <Metric label="Impressions" value={String(metrics.impressions)} />
        <Metric label="Clics" value={String(metrics.clicks)} />
        <Metric label="Taux de clic" value={`${(metrics.ctr * 100).toFixed(2)} %`} />
      </View>
      {metrics.truncated ? <Text style={styles.warning}>Les statistiques affichées sont limitées aux événements disponibles.</Text> : null}

      <View style={styles.panel}>
        <Text style={styles.sectionTitle}>Contenu et ciblage</Text>
        <Text style={styles.body}>{campaign.body}</Text>
        <View style={styles.infoGrid}>
          <InfoGroup label="Matières" value={formatSubjectList(campaign.targetSubjects)} />
          <InfoGroup label="Filières" value={formatTrackList(campaign.targetTracks)} />
          <InfoGroup label="Début de diffusion" value={formatControlDate(campaign.startsAt)} />
          <InfoGroup label="Fin de diffusion" value={formatControlDate(campaign.endsAt, "Sans date de fin")} />
          <InfoGroup label="Fréquence" value={`${campaign.frequencyCap.maxImpressions} impression${campaign.frequencyCap.maxImpressions === 1 ? "" : "s"} par personne / ${campaign.frequencyCap.windowHours} h`} />
          {campaign.ctaUrl ? <InfoGroup label="Bouton" value={campaign.ctaLabel || "Voir"} /> : null}
        </View>
        {editable ? <Pressable accessibilityRole="button" onPress={() => setEditing((value) => !value)} style={styles.secondaryAction}><Text style={styles.secondaryActionText}>{editing ? "Annuler l’édition" : "Modifier la campagne"}</Text></Pressable> : null}
        {editing ? <MonetizationCampaignForm busy={busy} initial={campaignInputFromAdmin(campaign)} onSubmit={save} submitLabel="Enregistrer les modifications" /> : null}
        <ControlDisclosure>
          <ControlId label="ID de la campagne" value={campaign.id} />
          <ControlId label="URL du média" value={campaign.mediaUrl} />
          <ControlId label="URL du bouton" value={campaign.ctaUrl} />
        </ControlDisclosure>
      </View>

      {canModerate && campaign.status !== "ended" ? <View style={styles.panel}>
        <Text style={styles.sectionTitle}>Validation de modération</Text>
        <Text style={styles.note}>La diffusion exige une campagne approuvée. Un rejet d’une campagne active la met automatiquement en pause.</Text>
        {campaign.moderationReason ? <Text style={styles.meta}>Dernier motif : {campaign.moderationReason}</Text> : null}
        <TextInput accessibilityLabel="Motif interne de la modération" multiline placeholder="Motif interne obligatoire" placeholderTextColor={text.muted} value={reviewReason} onChangeText={setReviewReason} style={[styles.input, styles.reason]} />
        <View style={styles.actions}>
          <Action label="Approuver" disabled={busy} onPress={() => void review("approved")} />
          <Action label="Rejeter" disabled={busy} onPress={() => void review("rejected")} secondary />
        </View>
      </View> : null}

      {canManage && transitions.length ? <View style={styles.panel}>
        <Text style={styles.sectionTitle}>Diffusion</Text>
        <Text style={styles.note}>Les changements de statut restent validés côté serveur. Une activation est impossible tant que la campagne n’est pas approuvée.</Text>
        <View style={styles.actions}>{transitions.map((status) => <Action key={status} disabled={busy || (status === "active" && campaign.moderationStatus !== "approved")} label={statusActionLabel(status)} secondary={status === "ended"} onPress={() => void setStatus(status)} />)}</View>
      </View> : null}

      <ControlDisclosure title="Historique technique">
        <ControlKeyValue label="Créée le" value={formatControlDate(campaign.createdAt)} />
        <ControlKeyValue label="Mise à jour le" value={formatControlDate(campaign.updatedAt)} />
        <ControlId label="UID du créateur" value={campaign.createdBy} />
        <ControlId label="UID du dernier éditeur" value={campaign.updatedBy} />
        <ControlId label="UID du modérateur" value={campaign.reviewedBy} />
      </ControlDisclosure>
    </ScrollView>
  );
}

function State({ children }: { children: React.ReactNode }) { return <View style={styles.state}>{children}</View>; }
function Metric({ label, value }: { label: string; value: string }) { return <View style={styles.metric}><Text style={styles.metricValue}>{value}</Text><Text style={styles.meta}>{label}</Text></View>; }
function InfoGroup({ label, value }: { label: string; value: string }) { return <View style={styles.infoGroup}><Text style={styles.infoLabel}>{label}</Text><Text style={styles.infoValue}>{value}</Text></View>; }
function Badge({ label }: { label: string }) { return <Text style={styles.badge}>{label}</Text>; }
function Action({ label, onPress, disabled, secondary }: { label: string; onPress: () => void; disabled?: boolean; secondary?: boolean }) { return <Pressable accessibilityRole="button" accessibilityState={{ disabled: Boolean(disabled) }} disabled={disabled} onPress={onPress} style={[styles.action, secondary && styles.actionSecondary, disabled && styles.disabled]}><Text style={[styles.actionText, secondary && styles.actionTextSecondary]}>{label}</Text></Pressable>; }
function statusActionLabel(status: SponsoredCampaignStatus) { return status === "active" ? "Activer la campagne" : status === "paused" ? "Mettre en pause" : status === "ended" ? "Terminer la campagne" : campaignStatusLabel(status); }

const styles = StyleSheet.create({
  state: { alignItems: "center", backgroundColor: surface.secondary, flex: 1, gap: spacing.md, justifyContent: "center", padding: spacing.xl },
  stateTitle: { color: text.primary, fontSize: 20, fontWeight: "800" },
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  back: { color: text.brand, fontWeight: "800" },
  header: { gap: spacing.sm, minWidth: 0 },
  kicker: { color: text.brand, fontSize: 12, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 28, fontWeight: "800", minWidth: 0 },
  subtitle: { color: text.secondary, minWidth: 0 },
  link: { color: text.brand, fontWeight: "700" },
  badges: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  badge: { backgroundColor: surface.selected, borderRadius: radius.pill, color: text.secondary, fontSize: 11, fontWeight: "800", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.md },
  metric: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, flexBasis: 140, flexGrow: 1, gap: spacing.xs, minWidth: 0, padding: spacing.lg },
  metricValue: { color: text.brand, fontSize: 28, fontWeight: "800" },
  panel: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, gap: spacing.md, minWidth: 0, padding: spacing.lg },
  sectionTitle: { color: text.primary, fontSize: 18, fontWeight: "800" },
  body: { color: text.primary, lineHeight: 22, minWidth: 0 },
  infoGrid: { flexDirection: "row", flexWrap: "wrap", gap: spacing.lg },
  infoGroup: { flexBasis: 180, flexGrow: 1, gap: spacing.xs, minWidth: 0 },
  infoLabel: { color: text.muted, fontSize: 11, fontWeight: "700" },
  infoValue: { color: text.primary, lineHeight: 21, minWidth: 0 },
  note: { color: text.secondary, lineHeight: 21 },
  meta: { color: text.muted, fontSize: 12, lineHeight: 18, minWidth: 0 },
  warning: { color: text.warning, fontSize: 12 },
  input: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, color: text.primary, minWidth: 0, padding: spacing.md, width: "100%" },
  reason: { minHeight: 90, textAlignVertical: "top" },
  actions: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  action: { alignItems: "center", backgroundColor: action.primary, borderRadius: radius.md, flexGrow: 1, justifyContent: "center", minHeight: 48, minWidth: 140, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  actionSecondary: { backgroundColor: surface.selected },
  actionText: { color: text.inverse, fontWeight: "800", textAlign: "center" },
  actionTextSecondary: { color: text.brand },
  secondaryAction: { alignItems: "center", alignSelf: "flex-start", borderColor: border.brand, borderRadius: radius.md, borderWidth: 1, minHeight: 44, justifyContent: "center", paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  secondaryActionText: { color: text.brand, fontWeight: "800" },
  disabled: { opacity: 0.45 },
});
